/**
 * Monta la disposicion completa: generaciones, orden dentro de cada fila,
 * posiciones de partida y asentamiento de la fisica.
 *
 * Se llama una vez al cargar un arbol y el resultado se guarda. Durante el
 * pan y el zoom no se recalcula nada: el dibujo lee las posiciones ya
 * resueltas. Solo se vuelve a simular cuando alguien arrastra un nodo o
 * cuando cambian los datos.
 */
import { calcularGeneraciones, type Generaciones } from "../core/generaciones.js";
import type { Indice } from "../core/grafo.js";
import { Fisica } from "./fisica.js";
import { ALTO_FILA } from "./medidas.js";
import { ordenar, separacionesPorFila, xIniciales, type Ordenacion } from "./orden.js";

/**
 * El nudo de una familia: el punto medio entre los progenitores, del que
 * cuelga todo lo demas.
 *
 * Dibujar padre -> hijo directamente llena el lienzo de lineas repetidas y
 * cruzadas. Con un nudo por familia, los dos progenitores se unen por una
 * barra horizontal, del centro de esa barra baja UN tronco hasta la barra de
 * hermandad, y de ahi cae una linea corta a cada hijo. Es el dibujo clasico
 * de un arbol genealogico, y dice de un vistazo dos cosas que antes no se
 * entendian: quienes son pareja y quienes son hermanos entre si.
 */
export interface Nudo {
  familia: string;
  /** Los progenitores presentes en el arbol (uno o dos). */
  progenitores: string[];
  hijos: string[];
  /** Fila de los progenitores. */
  fila: number;
  /** Centro horizontal, recalculado al moverse los padres. */
  x: number;
  y: number;
  /** Tipo de union, para dibujar el vinculo de forma distinta. */
  rel: string;
}

export interface Disposicion {
  fisica: Fisica;
  generaciones: Generaciones;
  orden: Ordenacion;
  nudos: Nudo[];
  /** Cruces de lineas que quedaron tras minimizar. */
  cruces: number;
  /** Cuantos pasos de fisica costo asentarse. */
  pasosAsentado: number;
  energiaFinal: number;
  /** Por que se paro: alcanzo el reposo, el tope de pasos o el de tiempo. */
  motivoParada: "reposo" | "pasos" | "tiempo";
}

export function disponer(
  ix: Indice,
  etiqueta?: (h: string) => string,
): Disposicion {
  const generaciones = calcularGeneraciones(ix, etiqueta);
  const orden = ordenar(ix, generaciones.fila);
  const xIni = xIniciales(orden, ix);

  const porFila = orden.filas.map((bloques) => bloques.flatMap((b) => b.miembros));
  const bloquesPorFila = orden.filas.map((bloques) => bloques.map((b) => b.miembros));
  const seps = separacionesPorFila(orden, ix);
  const fisica = new Fisica(ix, generaciones.fila, xIni, porFila, bloquesPorFila, seps);

  const nudos: Nudo[] = [];
  for (const fam of ix.arbol.familias.values()) {
    const progenitores = [fam.padre, fam.madre].filter(
      (p): p is string => !!p && fisica.nodos.has(p),
    );
    const hijos = fam.hijos.map((r) => r.hlink).filter((h) => fisica.nodos.has(h));
    // Una familia sin progenitores solo declara hermandad: no hay nudo del
    // que colgar nada, los hermanos se unen entre si al dibujar.
    if (!progenitores.length) continue;
    const fila = fisica.nodos.get(progenitores[0] as string)?.fila ?? 0;
    nudos.push({
      familia: fam.handle,
      progenitores,
      hijos,
      fila,
      x: 0,
      // El nudo va al centro vertical de la fila de los progenitores: de ahi
      // sale la barra que los une y de ahi baja el tronco hacia los hijos,
      // que es como se dibuja un arbol genealogico de toda la vida.
      y: fila * ALTO_FILA,
      rel: fam.rel ?? "Unknown",
    });
  }

  const { pasos, energia, motivo } = fisica.asentar();
  actualizarNudos(fisica, nudos);

  return {
    fisica,
    generaciones,
    orden,
    nudos,
    cruces: orden.cruces,
    pasosAsentado: pasos,
    energiaFinal: energia,
    motivoParada: motivo,
  };
}

/**
 * Recoloca los nudos a partir de donde estan los progenitores.
 *
 * Es barato a proposito: se llama tras cada paso de fisica, asi que no puede
 * hacer nada mas que una media.
 */
export function actualizarNudos(fisica: Fisica, nudos: Nudo[]): void {
  for (const n of nudos) {
    let s = 0;
    let c = 0;
    for (const p of n.progenitores) {
      const nodo = fisica.nodos.get(p);
      if (nodo) {
        s += nodo.x;
        c++;
      }
    }
    if (c) n.x = s / c;
  }
}

/** Avanza la simulacion y recoloca los nudos. Devuelve la energia restante. */
export function pasoDisposicion(d: Disposicion, dt = 1): number {
  const e = d.fisica.paso(dt);
  actualizarNudos(d.fisica, d.nudos);
  return e;
}
